import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "@aix/ui"
import { trpc } from "~/lib/trpc"
import { Plus, ExternalLink } from "lucide-react"
import CreateFromTemplateModal from "./components/CreateFromTemplateModal"

export default function CoachTemplatesTab() {
  const navigate = useNavigate()
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined)
  const [typeFilter, setTypeFilter] = useState<"pillar" | "side-quest" | undefined>(undefined)
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const { data, isLoading } = trpc.coachTemplates.list.useQuery({
    category: categoryFilter as any,
    type: typeFilter,
  })

  const templates = data?.templates || []

  const categories = [
    { value: undefined, label: "All" },
    { value: "health", label: "🏃 Health" },
    { value: "wealth", label: "💰 Wealth" },
    { value: "relationships", label: "❤️ Relationships" },
    { value: "growth", label: "🌱 Growth" },
    { value: "career", label: "💼 Career" },
    { value: "project", label: "🎯 Project" },
  ]

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b-2 border-border bg-secondary p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-primary">📦 Coach Templates</h2>
            <p className="text-sm text-muted-foreground mt-1">Manage and create coach templates for easy cloning</p>
          </div>
          <Button
            onClick={() => navigate({ to: "/coach-os/templates/new" })}
            className="bg-primary text-primary-foreground"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mt-4">
          {/* Category Filter */}
          <div className="flex gap-2">
            {categories.map((cat) => (
              <button
                key={cat.label}
                onClick={() => setCategoryFilter(cat.value)}
                className={`px-3 py-1 rounded-md text-sm transition-colors ${
                  categoryFilter === cat.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-background border-2 border-border hover:bg-secondary"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Type Filter */}
          <div className="flex gap-2 ml-4">
            <button
              onClick={() => setTypeFilter(undefined)}
              className={`px-3 py-1 rounded-md text-sm transition-colors ${
                typeFilter === undefined
                  ? "bg-primary text-primary-foreground"
                  : "bg-background border-2 border-border hover:bg-secondary"
              }`}
            >
              All Types
            </button>
            <button
              onClick={() => setTypeFilter("pillar")}
              className={`px-3 py-1 rounded-md text-sm transition-colors ${
                typeFilter === "pillar"
                  ? "bg-primary text-primary-foreground"
                  : "bg-background border-2 border-border hover:bg-secondary"
              }`}
            >
              🏛️ Pillar
            </button>
            <button
              onClick={() => setTypeFilter("side-quest")}
              className={`px-3 py-1 rounded-md text-sm transition-colors ${
                typeFilter === "side-quest"
                  ? "bg-primary text-primary-foreground"
                  : "bg-background border-2 border-border hover:bg-secondary"
              }`}
            >
              🎯 Side Quest
            </button>
          </div>
        </div>
      </div>

      {/* Template List */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="text-center text-muted-foreground py-12">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">
            <p className="text-lg mb-2">No templates found</p>
            <p className="text-sm">Create your first template to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className="bg-secondary border-2 border-border rounded-lg p-5 hover:border-primary transition-colors group"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl">{template.icon}</span>
                    <div>
                      <h3 className="font-bold text-primary">{template.name}</h3>
                      <p className="text-xs text-muted-foreground">v{template.version}</p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {template.description || "No description"}
                </p>

                {/* Meta */}
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                  <div className="flex gap-2">
                    <span
                      className={`px-2 py-1 rounded-md ${
                        template.type === "pillar"
                          ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                          : "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"
                      }`}
                    >
                      {template.type === "pillar" ? "🏛️ Pillar" : "🎯 Side Quest"}
                    </span>
                    <span className="px-2 py-1 rounded-md bg-background border border-border">
                      {template.defaultTone}
                    </span>
                  </div>
                  <span className="font-medium">
                    {template.timesCloned || 0} {template.timesCloned === 1 ? "clone" : "clones"}
                  </span>
                </div>

                {/* Duration (for side quests) */}
                {template.type === "side-quest" && template.defaultDurationWeeks && (
                  <div className="mb-4 text-xs text-muted-foreground">📅 {template.defaultDurationWeeks} weeks</div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setSelectedTemplate(template)
                      setIsCreateModalOpen(true)
                    }}
                    className="flex-1 bg-primary text-primary-foreground border-2 border-border"
                    size="sm"
                  >
                    ✨ Use Template
                  </Button>
                  <Button
                    onClick={() => navigate({ to: `/coach-os/templates/${template.id}` })}
                    variant="outline"
                    className="border-2 border-border"
                    size="sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create from Template Modal */}
      {selectedTemplate && (
        <CreateFromTemplateModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false)
            setSelectedTemplate(null)
          }}
          template={selectedTemplate}
        />
      )}
    </div>
  )
}
